/**
 * components/pricing/section-icons.ts
 *
 * One glyph per pricing decision, shared by every surface that names it.
 *
 * The strategy editor lists its sixteen sections down a navigator, and where
 * the editor is too narrow for their names it draws them as a strip of icons
 * alone; the wizard walks the same ground one screen at a time. Down the list
 * a section is found by its glyph rather than by reading every title, and in
 * the strip the glyph is all there is — which is the only justification the
 * design rules accept, since "icons are functional, not decorative".
 *
 * Two consequences follow from that justification, and both are enforced by
 * `section-icons.test.ts`. **No two sections may share a glyph**: a repeated
 * icon distinguishes nothing and is decoration by definition — which is why
 * Day Adjustments and Holidays no longer both wear `CalendarDays`. And **an
 * icon that names a section names it everywhere**: the navigator row, the
 * section's heading, the summary row, the wizard step header and the review
 * recap read this table rather than importing an icon each, or the same
 * decision would be a different picture on each screen an operator meets it on.
 *
 * The keys are the editor's own section keys (`EDITOR_SECTIONS`), so there is
 * no third vocabulary to keep in step; `editor-sections.ts` declares which icon
 * each section wears and the type there makes a new section pick one.
 */

import {
    Banknote,
    BedDouble,
    Binoculars,
    CalendarClock,
    CalendarDays,
    CalendarHeart,
    CalendarPlus,
    ChartSpline,
    Grid3X3,
    Lock,
    Puzzle,
    Ruler,
    Share2,
    Target,
    Timer,
    Users,
    type LucideIcon,
} from "lucide-react";

export const PRICING_SECTION_ICONS = {
    /** The room types the PMS delivers, and how many rooms and beds each has. */
    roomKindStructure: BedDouble,
    /** The goal every number after it is pursued for. */
    strategyPosture: Target,
    /**
     * Competitor rates and the weather forecast, watched. Binoculars rather
     * than a radar sweep, which is concentric rings and so the same shape as
     * the posture target one row above it.
     */
    marketInfluence: Binoculars,
    /** What a Friday, Saturday and Sunday is worth next to a midweek night. */
    dayAdjustments: CalendarDays,
    /** The base, minimum and maximum price of each room type. */
    keyMetrics: Banknote,
    /** What one, two or more guests pay in the same room. */
    roomStructure: Users,
    /** How far ahead each room type is booked. */
    bookingWindow: CalendarClock,
    /** The rounding step every suggested price lands on. */
    priceGranularity: Grid3X3,
    /** How long a stay has to be — a length, measured. */
    rateRestrictions: Ruler,
    /**
     * The countdown to arrival — a stopwatch rather than a clock face, since
     * `bookingWindow` is already a calendar carrying one, and how far ahead a
     * guest books is the neighbour this is most easily confused with.
     */
    lastMinute: Timer,
    /** The orphan night that fits between two reservations. */
    gapFilling: Puzzle,
    /**
     * The shape of the year, which is what the section actually draws. A
     * calendar would be the obvious pick and is the wrong one: `CalendarRange`
     * and the `CalendarDays` on Day Adjustments are one glyph at 16px.
     */
    seasonality: ChartSpline,
    /** The dates a property is booked for rather than on. */
    holidays: CalendarHeart,
    /**
     * The ordinary nights around a holiday break: the one bridging two premium
     * nights, and the shoulders right before and after it.
     */
    bridgeDays: CalendarPlus,
    /** Prices pushed out to a channel's own rate plan. */
    channelMapping: Share2,
    /** A cheaper rate the guest cannot cancel. */
    nonRefundable: Lock,
} as const satisfies Record<string, LucideIcon>;

export type PricingSectionIconKey = keyof typeof PRICING_SECTION_ICONS;
